import api from "../../services/api";
import { Eventbus } from "../../utils";
import { events } from "../../utils/constants";
import logger from "../../utils/logger";
import { format } from 'date-fns-tz';

const ExportService = (function () {
    // Private data cache
    let data = [];
    let isListenerRegistered = false;
    let isLoading = false; // Add loading flag to prevent duplicate requests

    function getData() {
        return [...data]; // Return a copy to prevent external mutation
    }

    function updateExport(updatedRecord) {
        try {
            if (!updatedRecord || !updatedRecord.id) {
                logger.warn('Invalid export record received for update:', updatedRecord);
                return;
            }

            const existingRecordIndex = data.findIndex(record => record.id === updatedRecord.id);

            if (existingRecordIndex !== -1) {
                return;
            } else {
                // Add new record
                data.push(updatedRecord);
                logger.debug('Added new export record:', updatedRecord.id);
            }

            Eventbus.emit(events.EXPORT_UPDATED, [...data]); // Emit updated data
        } catch (error) {
            logger.error('Error updating export record:', error, updatedRecord);
        }
    }

    function updateDeletedExport(deletedId) {
        try {
            if (!deletedId) {
                logger.warn('Invalid export ID received for deletion:', deletedId);
                return;
            }

            data = data.filter(record => record.id !== deletedId);
            logger.debug('Deleted export record:', deletedId);
            Eventbus.emit(events.EXPORT_UPDATED, [...data]); // Emit updated data
        } catch (error) {
            logger.error('Error deleting export record:', error, deletedId);
        }
    }

    function loadData(forceRefresh = false) {
        // Prevent duplicate requests
        if (isLoading && !forceRefresh) {
            return Promise.resolve([...data]);
        }

        // If data exists and we're not forcing a refresh, return the cached data
        if (data.length > 0 && !forceRefresh) {
            isLoading = false; // Ensure loading state is reset when returning cached data
            Eventbus.emit(events.EXPORT_LOADED, [...data]);
            return Promise.resolve([...data]);
        }

        isLoading = true;

        // Otherwise fetch fresh data from the API
        return api
            .getExports()
            .then((response) => {
                if (response.code === 'ERR_CANCELED') {
                    return data; // Return existing data on cancellation
                }
                data = response || []; // Store the response data in the data variable
                Eventbus.emit(events.EXPORT_LOADED, [...data]);

                // Only register the event listener once
                if (!isListenerRegistered) {
                    Eventbus.on(events.SIGNALR.WINZ_EXPORT_DATA_CHANGES, updateExport);
                    Eventbus.on(events.SIGNALR.WINZ_EXPORT_DELETED, updateDeletedExport);
                    isListenerRegistered = true;
                    logger.debug('SignalR centre data changes listener registered');
                }

                return [...data];
            })
            .catch((error) => {
                logger.error('Error fetching export records:', error);
                return [];
            })
            .finally(() => {
                isLoading = false;
            });
    }

    function init() {
        return loadData(true);
    }

    function clear() {
        if (isListenerRegistered) {
            Eventbus.off(updateExport);
            isListenerRegistered = false;
            logger.debug('SignalR export data changes listener removed');
        }
        data = [];
    }


    function createExport(postingDate) {
        return api.createExport(postingDate)
            .then((response) => {
                if (response.code === 'ERR_CANCELED') {
                    return null; // Return null on cancellation
                }
                logger.debug('Export created successfully:', response);
                return response; // Return the created export data
            })
            .catch((error) => {
                logger.error('Error creating export:', error);
                throw error; // Propagate the error
            });
    }

    function getDocument(id, finance = false) {
        return finance ? api.getFinanceDocument(id) : api.getCMSDocument(id)
            .then((response) => {
                if (response.code === 'ERR_CANCELED') {
                    return null; // Return null on cancellation
                }
                logger.debug('Document retrieved successfully:', response);
                return response; // Return the document data
            })
            .catch((error) => {
                logger.error('Error retrieving Document:', error);
                throw error; // Propagate the error
            });
    }

    async function downloadExportDocument(exportRecord, finance = false) {
        try {
            const fileData = await getDocument(exportRecord?.id, finance);
            if (!fileData) return;
            // Try to get the export record for filename/date
            let filename = `winz_export${finance ? '_finance' : '_cms'}.csv`;
            if (exportRecord && exportRecord.postingDate) {
                const formattedDate = format(exportRecord.postingDate, 'yyyy-MM-dd', { timeZone: 'Pacific/Auckland' });
                filename = `${filename}_${formattedDate}.csv`;
            }
            const blob = new Blob([fileData], { type: "text/csv" });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = filename;
            a.click();
            window.URL.revokeObjectURL(url);
            a.remove(); // Clean up the anchor element
        } catch (error) {
            // eslint-disable-next-line no-console
            logger.error("Error downloading export file:", error);
            throw error;
        }
    }

    function getLoadingState() {
        return isLoading;
    }

    function deleteExport(id) {
        return api.deleteExport(id)
            .then((response) => {
                if (response.code === 'ERR_CANCELED') {
                    return null; // Return null on cancellation
                }
                logger.debug('Export deleted successfully:', response);
                // Remove the deleted export from the local data cache
                data = data.filter(record => record.id !== id);
                Eventbus.emit(events.EXPORT_DELETED, id); // Emit deletion event
                return response; // Return the deletion response
            })
            .catch((error) => {
                logger.error('Error deleting export:', error);
                throw error; // Propagate the error
            });
    }

    function clear() {
        if (isListenerRegistered) {
            Eventbus.off(updateExport);
            Eventbus.off(updateDeletedExport);
            isListenerRegistered = false;
            logger.debug('SignalR export data changes listener removed');
        }
        data = [];
        isLoading = false; // Reset loading state
    }

    return {
        getData,
        loadData,
        init,
        clear,
        updateExport, // For SignalR updates
        getLoadingState, // Export loading state
        updateDeletedExport,
        createExport,
        downloadExportDocument,
        deleteExport,
        clear
    };
})();

export default ExportService;
